package com.sweetlime.dungeonamigo.presentation.features.character

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.material3.TabRowDefaults
import androidx.compose.material3.TabRowDefaults.tabIndicatorOffset
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow
import com.sweetlime.dungeonamigo.cache.Spells
import com.sweetlime.dungeonamigo.compendium.CompendiumRepository
import com.sweetlime.dungeonamigo.model.Character
import com.sweetlime.dungeonamigo.model.CharacterCalculator
import com.sweetlime.dungeonamigo.model.Condition
import com.sweetlime.dungeonamigo.model.InventoryItem
import com.sweetlime.dungeonamigo.presentation.features.compendium.SpellDetailScreen
import com.sweetlime.dungeonamigo.presentation.features.dice.DiceViewModel
import com.sweetlime.dungeonamigo.presentation.features.dice.DiceRollerSheet
import com.sweetlime.dungeonamigo.presentation.navigation.NavigationController
import com.sweetlime.dungeonamigo.presentation.ui.components.CharacterPortrait
import com.sweetlime.dungeonamigo.presentation.ui.components.CompendiumSyncIllustration
import com.sweetlime.dungeonamigo.presentation.ui.components.EmptyStateView
import com.sweetlime.dungeonamigo.presentation.ui.components.PortraitPickerSheet
import com.sweetlime.dungeonamigo.presentation.ui.components.PortraitSize
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import com.sweetlime.dungeonamigo.presentation.ui.theme.classColorFor
import com.sweetlime.dungeonamigo.presentation.ui.theme.pixelFont
import com.sweetlime.dungeonamigo.presentation.ui.theme.silkFont
import com.sweetlime.dungeonamigo.util.currentTimeMillis
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import com.sweetlime.dungeonamigo.presentation.features.session.SessionState
import com.sweetlime.dungeonamigo.presentation.features.session.SessionViewModel
import org.koin.compose.koinInject

data class CharacterSheetScreen(val characterId: String) : Screen {
    @Composable
    override fun Content() {
        val navigator = LocalNavigator.currentOrThrow
        val vm: CharacterViewModel = koinInject()
        val diceVm: DiceViewModel = koinInject()
        val sessionVm: SessionViewModel = koinInject()
        val navController: NavigationController = koinInject()
        val compendiumRepo: CompendiumRepository = koinInject()
        val character by vm.selectedCharacter.collectAsState()

        // Re-run loadCharacter whenever the top navigator screen changes so that
        // returning from SpellDetailScreen picks up any Firestore writes it made.
        LaunchedEffect(characterId, navigator.lastItem) { vm.loadCharacter(characterId) }

        // Register this character as "active" so CompendiumScreen can carry the ID
        // into SpellDetailScreen when the user browses spells from here.
        DisposableEffect(characterId) {
            navController.setActiveCharacter(characterId)
            onDispose { navController.setActiveCharacter(null) }
        }

        // Auto-post dice rolls to session feed when a session is active
        DisposableEffect(Unit) {
            diceVm.onRollCompleted = { roll, label ->
                if (sessionVm.sessionState.value is SessionState.InSession) {
                    sessionVm.postRollToFeed(roll, label)
                }
            }
            onDispose { diceVm.onRollCompleted = null }
        }

        var showDiceSheet by remember { mutableStateOf(false) }
        var diceSheetLabel by remember { mutableStateOf("") }
        var showSpellPicker by remember { mutableStateOf(false) }
        var showPortraitPicker by remember { mutableStateOf(false) }
        var showDeleteDialog by remember { mutableStateOf(false) }

        val char = character
        if (char == null) {
            Box(Modifier.fillMaxSize().background(BrandColors.Background), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = BrandColors.Parchment)
            }
            return
        }

        val pagerState    = rememberPagerState { 5 }
        val coroutineScope = rememberCoroutineScope()
        val tabTitles     = listOf("Stats", "Skills", "Spells", "Inventory", "Notes")

        Box(
            Modifier
                .fillMaxSize()
                .background(BrandColors.Background)
                .windowInsetsPadding(WindowInsets.systemBars),
        ) {
            Column(Modifier.fillMaxSize()) {
                // ── Top bar ────────────────────────────────────────────────
                CharacterTopBar(
                    character         = char,
                    onBack            = { navigator.pop() },
                    onAdjustHp        = { delta -> vm.adjustHp(delta) },
                    onSetHp           = { hp, tmp -> vm.setHp(hp, tmp) },
                    onToggleCondition = { vm.toggleCondition(it) },
                    onPortraitTap     = { showPortraitPicker = true },
                    onDelete          = { showDeleteDialog = true },
                )

                // ── Tabs ────────────────────────────────────────────────
                TabRow(
                    selectedTabIndex = pagerState.currentPage,
                    containerColor   = BrandColors.Surface,
                    contentColor     = BrandColors.SpellBlue,
                    indicator = { tabPositions ->
                        TabRowDefaults.SecondaryIndicator(
                            modifier  = Modifier.tabIndicatorOffset(tabPositions[pagerState.currentPage]),
                            height    = 2.5.dp,
                            color     = BrandColors.SpellBlue,
                        )
                    },
                ) {
                    tabTitles.forEachIndexed { i, title ->
                        val selected = pagerState.currentPage == i
                        Tab(
                            selected  = selected,
                            onClick   = { coroutineScope.launch { pagerState.animateScrollToPage(i) } },
                            selectedContentColor   = BrandColors.TextPrimary,
                            unselectedContentColor = BrandColors.SlateLight,
                        ) {
                            Text(title, fontSize = 13.sp, modifier = Modifier.padding(vertical = 12.dp))
                        }
                    }
                }

                HorizontalPager(state = pagerState, modifier = Modifier.weight(1f)) { page ->
                    when (page) {
                        0 -> StatsTab(
                            character    = char,
                            onAbilityClick = { abilityName, score ->
                                diceVm.preConfigureForAbility(abilityName, CharacterCalculator.modifier(score))
                                diceSheetLabel = "$abilityName Check"
                                showDiceSheet = true
                            },
                        )
                        1 -> SkillsTab(
                            character      = char,
                            onToggleSkill  = { vm.toggleSkillProficiency(it) },
                            onToggleSave   = { vm.toggleSavingThrowProficiency(it) },
                            onSkillRoll    = { skillName, bonus ->
                                diceVm.preConfigureForSkill(skillName, bonus)
                                diceSheetLabel = skillName
                                showDiceSheet = true
                            },
                        )
                        2 -> SpellsTab(
                            character        = char,
                            compendiumRepo   = compendiumRepo,
                            onToggleSlot     = { level, idx -> vm.toggleSpellSlotUsed(level, idx) },
                            onRemoveSpell    = { slug -> vm.removeSpell(slug) },
                            onTogglePrepared = { slug -> vm.togglePrepared(slug) },
                            onBrowseSpells   = { showSpellPicker = true },
                        )
                        3 -> InventoryTab(char,
                            onAddItem    = { vm.addInventoryItem(it) },
                            onRemoveItem = { vm.removeInventoryItem(it) })
                        4 -> NotesTab(char, onSave = { vm.saveNotes(it) })
                    }
                }
            }

            // ── Dice FAB (hidden on inventory tab to avoid overlap) ────────
            if (pagerState.currentPage != 3) {
                FloatingActionButton(
                    onClick = {
                        diceVm.clearDice()
                        diceSheetLabel = ""
                        showDiceSheet = true
                    },
                    modifier       = Modifier
                        .align(Alignment.BottomEnd)
                        .padding(bottom = 16.dp, end = 16.dp)
                        .size(58.dp),
                    containerColor = BrandColors.StoneLight,
                    contentColor   = BrandColors.Parchment,
                    elevation      = FloatingActionButtonDefaults.elevation(defaultElevation = 6.dp),
                ) {
                    Text("⚄", fontSize = 26.sp)
                }
            }
        }

        if (showDiceSheet) {
            DiceRollerSheet(
                vm        = diceVm,
                label     = diceSheetLabel,
                onDismiss = { showDiceSheet = false },
            )
        }

        if (showSpellPicker) {
            SpellPickerSheet(
                character      = char,
                compendiumRepo = compendiumRepo,
                onAddSpell     = { vm.addSpell(it) },
                onRemoveSpell  = { vm.removeSpell(it) },
                onDismiss      = { showSpellPicker = false },
            )
        }

        if (showPortraitPicker) {
            PortraitPickerSheet(
                characterClass = char.characterClass,
                initialPrimary = char.portraitPrimaryColor,
                initialSecondary = char.portraitSecondaryColor,
                onSave = { primary, secondary -> vm.updatePortraitColors(primary, secondary) },
                onDismiss = { showPortraitPicker = false },
            )
        }

        if (showDeleteDialog) {
            AlertDialog(
                onDismissRequest = { showDeleteDialog = false },
                containerColor   = BrandColors.Surface,
                title            = { Text("Delete Character?", color = BrandColors.TextPrimary) },
                text             = {
                    Text(
                        "\"${char.name.ifBlank { "Unnamed Hero" }}\" will be permanently deleted. This cannot be undone.",
                        color = BrandColors.TextSecondary,
                    )
                },
                confirmButton = {
                    TextButton(onClick = { vm.deleteCharacter { navigator.pop() } }) {
                        Text("Delete", color = BrandColors.Ember)
                    }
                },
                dismissButton = {
                    TextButton(onClick = { showDeleteDialog = false }) {
                        Text("Cancel", color = BrandColors.TextSecondary)
                    }
                },
            )
        }
    }
}

// ── Top bar ────────────────────────────────────────────────────────────────────

@Composable
private fun CharacterTopBar(
    character: Character,
    onBack: () -> Unit,
    onAdjustHp: (Int) -> Unit,
    onSetHp: (Int, Int) -> Unit,
    onToggleCondition: (String) -> Unit,
    onPortraitTap: () -> Unit = {},
    onDelete: () -> Unit = {},
) {
    var showSetHpDialog     by remember { mutableStateOf(false) }
    var showConditionPicker by remember { mutableStateOf(false) }
    val hpColor  = CharacterCalculator.hpColor(character.currentHp, character.maxHp)
    val pf = pixelFont()
    val sf = silkFont()

    Column(
        Modifier
            .fillMaxWidth()
            .background(BrandColors.Surface)
            .padding(horizontal = 8.dp, vertical = 8.dp),
    ) {
        // ── Row 1: back | name + subtitle | delete ──────────────────────
        Row(verticalAlignment = Alignment.CenterVertically) {
            IconButton(onClick = onBack) {
                Icon(Icons.Filled.ArrowBack, "Back", tint = BrandColors.SlateLight)
            }
            Column(Modifier.weight(1f)) {
                Text(
                    text       = character.name.ifBlank { "Unnamed Hero" },
                    color      = BrandColors.TextPrimary,
                    fontFamily = pf,
                    fontWeight = FontWeight.Bold,
                    fontSize   = 22.sp,
                )
                // "Human Barbarian · Lv 1" with level in Parchment
                val raceClass = buildString {
                    if (character.race.isNotBlank()) append(character.race)
                    if (character.characterClass.isNotBlank()) {
                        if (isNotEmpty()) append(" ")
                        append(character.characterClass)
                    }
                }
                if (raceClass.isNotBlank() || character.level > 0) {
                    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        if (raceClass.isNotBlank()) {
                            Text(raceClass, color = BrandColors.IvoryDim, fontSize = 12.sp)
                            if (character.level > 0) Text("·", color = BrandColors.IvoryDim, fontSize = 12.sp)
                        }
                        if (character.level > 0) {
                            Text("Lv ${character.level}", color = BrandColors.Parchment, fontFamily = pf, fontSize = 12.sp)
                        }
                    }
                }
            }
            // Delete button — Slate border, 40dp
            Box(
                modifier = Modifier
                    .size(40.dp)
                    .clip(RoundedCornerShape(8.dp))
                    .border(1.dp, BrandColors.Slate.copy(alpha = 0.5f), RoundedCornerShape(8.dp))
                    .clickable(onClick = onDelete),
                contentAlignment = Alignment.Center,
            ) {
                Text("✕", color = BrandColors.EmberDim, fontSize = 16.sp)
            }
            Spacer(Modifier.width(4.dp))
        }

        Spacer(Modifier.height(8.dp))

        // ── Row 2: avatar | HP counter ──────────────────────────────────
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(16.dp),
        ) {
            // 84dp avatar tile, tappable for portrait picker
            CharacterPortrait(
                characterClass = character.characterClass,
                avatarId       = character.avatarId,
                primaryColor   = Color(character.portraitPrimaryColor),
                secondaryColor = Color(character.portraitSecondaryColor),
                size           = PortraitSize.LARGE,
                modifier       = Modifier.clickable(onClick = onPortraitTap),
            )

            // HP counter
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Row(
                    verticalAlignment     = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    // − button — Ember-tinted, 46dp
                    Box(
                        modifier = Modifier
                            .size(46.dp)
                            .clip(RoundedCornerShape(10.dp))
                            .background(BrandColors.Ember.copy(alpha = 0.15f))
                            .border(1.dp, BrandColors.Ember.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                            .clickable { onAdjustHp(-1) },
                        contentAlignment = Alignment.Center,
                    ) {
                        Text("−", color = BrandColors.Ember, fontSize = 22.sp, fontWeight = FontWeight.Bold)
                    }

                    // HP number — long-press to set manually
                    Box(
                        modifier = Modifier
                            .weight(1f)
                            .combinedClickable(onLongClick = { showSetHpDialog = true }, onClick = {}),
                        contentAlignment = Alignment.Center,
                    ) {
                        Column(horizontalAlignment = Alignment.CenterHorizontally) {
                            Text(
                                text       = "${character.currentHp}",
                                color      = hpColor,
                                fontFamily = pf,
                                fontWeight = FontWeight.Bold,
                                fontSize   = 38.sp,
                            )
                            Text(
                                text       = "/ ${character.maxHp}",
                                color      = BrandColors.IvoryDim,
                                fontFamily = pf,
                                fontSize   = 15.sp,
                            )
                        }
                    }

                    // + button — Venom-tinted, 46dp
                    Box(
                        modifier = Modifier
                            .size(46.dp)
                            .clip(RoundedCornerShape(10.dp))
                            .background(BrandColors.Venom.copy(alpha = 0.15f))
                            .border(1.dp, BrandColors.Venom.copy(alpha = 0.5f), RoundedCornerShape(10.dp))
                            .clickable { onAdjustHp(1) },
                        contentAlignment = Alignment.Center,
                    ) {
                        Text("+", color = BrandColors.Venom, fontSize = 22.sp, fontWeight = FontWeight.Bold)
                    }
                }

                // Segmented HP bar (up to 16 cells)
                SegmentedHpBar(
                    current  = character.currentHp,
                    max      = character.maxHp,
                    hpColor  = hpColor,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }

        Spacer(Modifier.height(8.dp))

        // ── Row 3: conditions + TMP HP ──────────────────────────────────
        Row(
            Modifier.fillMaxWidth().padding(horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            if (character.temporaryHp > 0) {
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(12.dp))
                        .background(BrandColors.SpellBlue.copy(alpha = 0.15f))
                        .border(1.dp, BrandColors.SpellBlue.copy(alpha = 0.4f), RoundedCornerShape(12.dp))
                        .padding(horizontal = 8.dp, vertical = 3.dp),
                ) {
                    Text(
                        text       = "${character.temporaryHp} TMP",
                        color      = BrandColors.SpellBlue,
                        fontFamily = sf,
                        fontSize   = 10.sp,
                        fontWeight = FontWeight.Bold,
                    )
                }
            }
            character.conditions.forEach { condName ->
                val cond = Condition.entries.find { it.name == condName }
                Box(
                    modifier = Modifier
                        .clip(RoundedCornerShape(12.dp))
                        .background(BrandColors.Ember.copy(alpha = 0.13f))
                        .border(1.dp, BrandColors.Ember.copy(alpha = 0.4f), RoundedCornerShape(12.dp))
                        .clickable { onToggleCondition(condName) }
                        .padding(horizontal = 8.dp, vertical = 3.dp),
                ) {
                    Text(
                        text     = "${cond?.emoji ?: "⚡"} ${cond?.displayName ?: condName}",
                        color    = BrandColors.Ember,
                        fontSize = 11.sp,
                    )
                }
            }
            Spacer(Modifier.weight(1f))
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .border(1.dp, BrandColors.Slate.copy(alpha = 0.4f), RoundedCornerShape(8.dp))
                    .clickable { showConditionPicker = true }
                    .padding(horizontal = 10.dp, vertical = 4.dp),
            ) {
                Text("+ Condition", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
            }
        }
    }

    if (showSetHpDialog) {
        SetHpDialog(
            current   = character.currentHp,
            max       = character.maxHp,
            tempHp    = character.temporaryHp,
            onConfirm = { hp, tmp -> onSetHp(hp, tmp); showSetHpDialog = false },
            onDismiss = { showSetHpDialog = false },
        )
    }

    if (showConditionPicker) {
        ConditionPickerDialog(
            activeConditions = character.conditions,
            onToggle         = { cond -> onToggleCondition(cond) },
            onDismiss        = { showConditionPicker = false },
        )
    }
}

// Segmented HP bar — up to 16 cells, filled in HP color
@Composable
private fun SegmentedHpBar(current: Int, max: Int, hpColor: Color, modifier: Modifier = Modifier) {
    if (max <= 0) return
    val cellCount = minOf(max, 16)
    val filledCount = if (max <= 16) current else (current.toFloat() / max * 16).toInt()

    Row(
        modifier = modifier.height(14.dp),
        horizontalArrangement = Arrangement.spacedBy(2.dp),
    ) {
        repeat(cellCount) { i ->
            val filled = i < filledCount
            Box(
                modifier = Modifier
                    .weight(1f)
                    .fillMaxHeight()
                    .clip(RoundedCornerShape(2.dp))
                    .background(
                        if (filled) hpColor else BrandColors.StoneLight
                    )
                    .border(1.dp, BrandColors.Slate.copy(alpha = 0.4f), RoundedCornerShape(2.dp)),
            )
        }
    }
}

@Composable
private fun SetHpDialog(current: Int, max: Int, tempHp: Int, onConfirm: (Int, Int) -> Unit, onDismiss: () -> Unit) {
    var hpText  by remember { mutableStateOf("$current") }
    var tmpText by remember { mutableStateOf("$tempHp") }
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor   = BrandColors.Surface,
        title  = { Text("Set HP", color = BrandColors.TextPrimary) },
        text   = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(
                    value         = hpText,
                    onValueChange = { hpText = it.filter { c -> c.isDigit() } },
                    label         = { Text("Current HP (max $max)", color = BrandColors.TextSecondary) },
                    singleLine    = true,
                    colors        = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor   = BrandColors.Parchment,
                        unfocusedBorderColor = BrandColors.SurfaceLight,
                        focusedTextColor     = BrandColors.TextPrimary,
                        unfocusedTextColor   = BrandColors.TextPrimary,
                        cursorColor          = BrandColors.Parchment,
                    ),
                )
                OutlinedTextField(
                    value         = tmpText,
                    onValueChange = { tmpText = it.filter { c -> c.isDigit() } },
                    label         = { Text("Temporary HP", color = BrandColors.TextSecondary) },
                    singleLine    = true,
                    colors        = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor   = BrandColors.Parchment,
                        unfocusedBorderColor = BrandColors.SurfaceLight,
                        focusedTextColor     = BrandColors.TextPrimary,
                        unfocusedTextColor   = BrandColors.TextPrimary,
                        cursorColor          = BrandColors.Parchment,
                    ),
                )
            }
        },
        confirmButton = {
            TextButton(onClick = { onConfirm(hpText.toIntOrNull() ?: 0, tmpText.toIntOrNull() ?: 0) }) {
                Text("Set", color = BrandColors.Parchment)
            }
        },
        dismissButton = {
            TextButton(onClick = onDismiss) { Text("Cancel", color = BrandColors.TextSecondary) }
        },
    )
}

@Composable
private fun ConditionPickerDialog(
    activeConditions: List<String>,
    onToggle: (String) -> Unit,
    onDismiss: () -> Unit,
) {
    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor   = BrandColors.Surface,
        title  = { Text("Conditions", color = BrandColors.TextPrimary) },
        text   = {
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Condition.entries.forEach { cond ->
                    val active = cond.name in activeConditions
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(8.dp))
                            .background(if (active) BrandColors.Ember else BrandColors.SurfaceLight)
                            .clickable { onToggle(cond.name) }
                            .padding(8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Text(cond.emoji, fontSize = 18.sp)
                        Column {
                            Text(cond.displayName, color = BrandColors.TextPrimary, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                            Text(cond.shortDescription, color = BrandColors.TextSecondary, fontSize = 11.sp)
                        }
                    }
                }
            }
        },
        confirmButton = {
            TextButton(onClick = onDismiss) { Text("Done", color = BrandColors.Parchment) }
        },
    )
}

// ── Tab 1: Stats ───────────────────────────────────────────────────────────────

@Composable
private fun StatsTab(
    character: Character,
    onAbilityClick: (abilityName: String, score: Int) -> Unit,
) {
    val prof = CharacterCalculator.proficiencyBonus(character.level)

    val abilities = listOf(
        "Strength"     to character.strength,
        "Dexterity"    to character.dexterity,
        "Constitution" to character.constitution,
        "Intelligence" to character.intelligence,
        "Wisdom"       to character.wisdom,
        "Charisma"     to character.charisma,
    )

    Column(
        Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                abilities.take(3).forEach { (name, score) ->
                    AbilityCard(
                        name = name,
                        score = score,
                        hasSaveProficiency = name.lowercase() in character.savingThrowProficiencies,
                        onClick = { onAbilityClick(name, score) },
                    )
                }
            }
            Column(Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                abilities.drop(3).forEach { (name, score) ->
                    AbilityCard(
                        name = name,
                        score = score,
                        hasSaveProficiency = name.lowercase() in character.savingThrowProficiencies,
                        onClick = { onAbilityClick(name, score) },
                    )
                }
            }
        }

        val pf = pixelFont()
        Column(
            Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(14.dp))
                .background(BrandColors.Surface)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            Text("Combat", color = BrandColors.Parchment, fontFamily = pf, fontWeight = FontWeight.Bold, fontSize = 15.sp)
            HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceEvenly) {
                CombatStat("AC",         "${character.armorClass}",                              valueColor = BrandColors.SpellBlue)
                CombatStat("Initiative", CharacterCalculator.modifierString(character.dexterity))
                CombatStat("Speed",      "${character.speed} ft")
                CombatStat("Hit Dice",   character.hitDice.ifBlank { "—" })
            }
            HorizontalDivider(color = BrandColors.Slate.copy(alpha = 0.2f))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceEvenly) {
                CombatStat("Prof. Bonus",  "+$prof")
                CombatStat("Passive Perc.", "${10 + CharacterCalculator.skillBonus(character, "Perception")}")
                CombatStat("HP Max",        "${character.maxHp}",                                valueColor = BrandColors.Ember)
            }
        }
    }
}

@Composable
private fun AbilityCard(name: String, score: Int, hasSaveProficiency: Boolean, onClick: () -> Unit) {
    val pf = pixelFont()
    val sf = silkFont()
    val mod = CharacterCalculator.modifier(score)
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .background(BrandColors.Surface)
            .clickable(onClick = onClick)
            .padding(vertical = 12.dp, horizontal = 8.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(name.take(3).uppercase(), color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 8.sp)
        Text(
            text       = "$score",
            color      = BrandColors.Parchment,
            fontFamily = pf,
            fontSize   = 30.sp,
            fontWeight = FontWeight.Bold,
        )
        Text(
            text       = CharacterCalculator.modifierString(score),
            color      = if (mod >= 0) BrandColors.Venom else BrandColors.EmberDim,
            fontFamily = pf,
            fontSize   = 13.sp,
        )
        if (hasSaveProficiency) {
            Spacer(Modifier.height(4.dp))
            Box(
                Modifier
                    .size(6.dp)
                    .clip(RoundedCornerShape(1.dp))
                    .background(BrandColors.Parchment),
            )
        }
    }
}

@Composable
private fun CombatStat(label: String, value: String, valueColor: Color = BrandColors.TextPrimary) {
    val pf = pixelFont()
    val sf = silkFont()
    Column(horizontalAlignment = Alignment.CenterHorizontally) {
        Text(value, color = valueColor, fontFamily = pf, fontWeight = FontWeight.Bold, fontSize = 16.sp)
        Text(label, color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 8.sp)
    }
}

// ── Tab 2: Skills ──────────────────────────────────────────────────────────────

@Composable
private fun SkillsTab(
    character: Character,
    onToggleSkill: (String) -> Unit,
    onToggleSave: (String) -> Unit,
    onSkillRoll: (skillName: String, bonus: Int) -> Unit,
) {
    LazyColumn(
        modifier       = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        item {
            val pf = pixelFont()
            Text("Saving Throws", color = BrandColors.Parchment, fontFamily = pf, fontWeight = FontWeight.Bold, fontSize = 14.sp)
            Spacer(Modifier.height(6.dp))
        }
        items(CharacterCalculator.SAVING_THROWS) { ability ->
            val proficient = ability in character.savingThrowProficiencies
            val bonus      = CharacterCalculator.savingThrowBonus(character, ability)
            SkillRow(
                name       = ability.replaceFirstChar(Char::uppercase),
                ability    = "",
                bonus      = bonus,
                proficient = proficient,
                expert     = false,
                onClick    = { onToggleSave(ability) },
                onRoll     = { onSkillRoll("${ability.replaceFirstChar(Char::uppercase)} Save", bonus) },
            )
        }
        item {
            val pf = pixelFont()
            Spacer(Modifier.height(12.dp))
            Text("Skills", color = BrandColors.Parchment, fontFamily = pf, fontWeight = FontWeight.Bold, fontSize = 14.sp)
            Spacer(Modifier.height(6.dp))
        }
        items(CharacterCalculator.SKILLS.entries.toList()) { (skill, abilityKey) ->
            val proficient  = skill in character.skillProficiencies
            val expert      = skill in character.expertiseSkills
            val bonus       = CharacterCalculator.skillBonus(character, skill)
            val abilityAbbr = abilityKey.take(3).uppercase()
            SkillRow(
                name       = skill,
                ability    = abilityAbbr,
                bonus      = bonus,
                proficient = proficient,
                expert     = expert,
                onClick    = { onToggleSkill(skill) },
                onRoll     = { onSkillRoll(skill, bonus) },
            )
        }
    }
}

@Composable
private fun SkillRow(
    name: String,
    ability: String,
    bonus: Int,
    proficient: Boolean,
    expert: Boolean,
    onClick: () -> Unit,
    onRoll: () -> Unit,
) {
    val pf = pixelFont()
    val sf = silkFont()
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(6.dp))
            .clickable(onClick = onRoll)
            .padding(horizontal = 8.dp, vertical = 6.dp),
        verticalAlignment     = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        // Square proficiency indicator (tap to toggle)
        Box(
            modifier = Modifier
                .size(20.dp)
                .clickable(onClick = onClick),
            contentAlignment = Alignment.Center,
        ) {
            when {
                expert     -> Box(
                    Modifier.size(12.dp).clip(RoundedCornerShape(2.dp)).background(BrandColors.Parchment),
                    contentAlignment = Alignment.Center,
                ) { Text("★", color = BrandColors.Background, fontSize = 8.sp) }
                proficient -> Box(Modifier.size(12.dp).clip(RoundedCornerShape(2.dp)).background(BrandColors.Parchment))
                else       -> Box(Modifier.size(12.dp).clip(RoundedCornerShape(2.dp)).border(1.dp, BrandColors.Slate, RoundedCornerShape(2.dp)))
            }
        }
        Text(name, color = BrandColors.TextPrimary, fontSize = 13.sp, modifier = Modifier.weight(1f))
        if (ability.isNotBlank()) {
            Text(ability, color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
        }
        Text(
            text       = if (bonus >= 0) "+$bonus" else "$bonus",
            color      = if (proficient || expert) BrandColors.Parchment else BrandColors.IvoryDim,
            fontFamily = pf,
            fontWeight = FontWeight.Bold,
            fontSize   = 13.sp,
        )
    }
}

// ── Tab 3: Spells ──────────────────────────────────────────────────────────────

@Composable
private fun SpellsTab(
    character: Character,
    compendiumRepo: CompendiumRepository,
    onToggleSlot: (String, Int) -> Unit,
    onRemoveSpell: (String) -> Unit,
    onTogglePrepared: (String) -> Unit,
    onBrowseSpells: () -> Unit,
) {
    val navigator = LocalNavigator.currentOrThrow
    val isSpellcaster = CharacterCalculator.SPELLCASTING_ABILITY.containsKey(character.characterClass)

    // Resolve spell slugs to Spells objects (SQLDelight, synchronous)
    val resolvedSpells = remember(character.knownSpells) {
        character.knownSpells.mapNotNull { slug -> compendiumRepo.getSpellBySlug(slug) }
    }

    LazyColumn(
        modifier       = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(16.dp),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        // ── Spellcasting stats chips ──────────────────────────────────────
        if (isSpellcaster) {
            item {
                val spellcastingAbility = CharacterCalculator.SPELLCASTING_ABILITY[character.characterClass] ?: "intelligence"
                val spellAbilityScore   = CharacterCalculator.abilityScore(character, spellcastingAbility)
                val prof                = CharacterCalculator.proficiencyBonus(character.level)
                Row(
                    Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(10.dp))
                        .background(BrandColors.Surface)
                        .padding(12.dp),
                    horizontalArrangement = Arrangement.SpaceEvenly,
                ) {
                    CombatStat("Ability", spellcastingAbility.take(3).uppercase())
                    CombatStat("Save DC", "${CharacterCalculator.spellSaveDc(spellAbilityScore, prof)}")
                    CombatStat("Atk Bonus", "+${CharacterCalculator.spellAttackBonus(spellAbilityScore, prof)}")
                }
            }
        }

        // ── Section 1: Spell Slots ────────────────────────────────────────
        if (isSpellcaster) {
            if (character.spellSlots.isEmpty()) {
                item { Text("No spell slots at this level", color = BrandColors.TextSecondary, fontSize = 13.sp) }
            } else {
                items(character.spellSlots.entries.sortedBy { it.key }.toList()) { (level, slot) ->
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(10.dp))
                            .background(BrandColors.Surface)
                            .padding(12.dp),
                    ) {
                        Text("Level $level Slots", color = BrandColors.TextSecondary, fontSize = 12.sp)
                        Spacer(Modifier.height(8.dp))
                        Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                            for (i in 0 until slot.total) {
                                val isUsed = i < slot.used
                                Box(
                                    modifier = Modifier
                                        .size(28.dp)
                                        .clip(CircleShape)
                                        .background(if (isUsed) BrandColors.Ember else Color.Transparent)
                                        .border(2.dp, if (isUsed) BrandColors.Ember else BrandColors.Parchment, CircleShape)
                                        .clickable { onToggleSlot(level, i) },
                                )
                            }
                        }
                    }
                }
            }
        } else {
            item {
                Column(
                    Modifier.fillMaxWidth().padding(top = 40.dp),
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Text("🚫", fontSize = 40.sp)
                    Spacer(Modifier.height(8.dp))
                    Text("${character.characterClass} is not a spellcaster", color = BrandColors.TextSecondary)
                }
            }
            return@LazyColumn
        }

        // ── Section 2: Known Spells ───────────────────────────────────────
        item {
            Row(
                Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    "Known Spells",
                    color      = BrandColors.Parchment,
                    fontWeight = FontWeight.Bold,
                    fontSize   = 14.sp,
                    modifier   = Modifier.weight(1f),
                )
                TextButton(
                    onClick          = onBrowseSpells,
                    contentPadding   = PaddingValues(horizontal = 8.dp, vertical = 0.dp),
                ) {
                    Text("+ Add", color = BrandColors.SpellBlue, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
                }
            }
        }

        if (character.knownSpells.isEmpty()) {
            item {
                EmptyStateView(
                    illustration = { CompendiumSyncIllustration() },
                    headline = "No spells prepared",
                    subtext = "Browse the Compendium to add spells to your character",
                    ctaText = "Open Compendium",
                    onCtaClick = onBrowseSpells,
                    modifier = Modifier.fillMaxWidth().padding(vertical = 16.dp),
                )
            }
        } else {
            // Group by spell level
            val grouped = resolvedSpells.groupBy { it.level ?: "0" }
                .entries.sortedBy { (level, _) -> level.toIntOrNull() ?: 0 }

            grouped.forEach { (level, spells) ->
                item(key = "header_$level") {
                    Text(
                        text = spellLevelHeader(level),
                        color = BrandColors.TextSecondary,
                        fontSize = 12.sp,
                        fontWeight = FontWeight.SemiBold,
                        modifier = Modifier.padding(top = 8.dp, bottom = 2.dp),
                    )
                }
                items(spells, key = { it.slug }) { spell ->
                    val dismissState = rememberSwipeToDismissBoxState(
                        confirmValueChange = {
                            if (it == SwipeToDismissBoxValue.EndToStart) {
                                onRemoveSpell(spell.slug); true
                            } else false
                        },
                    )
                    SwipeToDismissBox(
                        state             = dismissState,
                        backgroundContent = {
                            Box(
                                Modifier
                                    .fillMaxSize()
                                    .clip(RoundedCornerShape(8.dp))
                                    .background(BrandColors.Ember)
                                    .padding(end = 16.dp),
                                contentAlignment = Alignment.CenterEnd,
                            ) {
                                Icon(Icons.Filled.Delete, "Remove spell", tint = BrandColors.TextPrimary)
                            }
                        },
                        modifier                   = Modifier.clip(RoundedCornerShape(8.dp)),
                        enableDismissFromStartToEnd = false,
                    ) {
                        KnownSpellRow(
                            spell      = spell,
                            isPrepared = spell.slug in character.preparedSpells,
                            onClick    = { navigator.push(SpellDetailScreen(spell.slug, character.id)) },
                            onTogglePrepared = { onTogglePrepared(spell.slug) },
                        )
                    }
                }
            }
        }

        item { Spacer(Modifier.height(72.dp)) }
    }
}

private fun spellLevelHeader(level: String): String = when (level.trim()) {
    "0", "cantrip", "Cantrip" -> "Cantrips"
    "1" -> "1st Level"
    "2" -> "2nd Level"
    "3" -> "3rd Level"
    "4" -> "4th Level"
    "5" -> "5th Level"
    "6" -> "6th Level"
    "7" -> "7th Level"
    "8" -> "8th Level"
    "9" -> "9th Level"
    else -> "Level $level"
}

@Composable
private fun KnownSpellRow(
    spell: Spells,
    isPrepared: Boolean,
    onClick: () -> Unit,
    onTogglePrepared: () -> Unit,
) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(BrandColors.Surface)
            .clickable(onClick = onClick)
            .padding(horizontal = 12.dp, vertical = 10.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        // Prepared indicator
        Box(
            modifier = Modifier
                .size(10.dp)
                .clip(CircleShape)
                .background(if (isPrepared) BrandColors.Parchment else Color.Transparent)
                .border(1.dp, if (isPrepared) BrandColors.Parchment else BrandColors.TextSecondary, CircleShape)
                .clickable(onClick = onTogglePrepared),
        )

        Text(
            text     = spell.name,
            color    = BrandColors.TextPrimary,
            fontSize = 14.sp,
            modifier = Modifier.weight(1f),
        )

        // School badge
        spell.school?.let { school ->
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(4.dp))
                    .background(BrandColors.SpellBlue.copy(alpha = 0.2f))
                    .padding(horizontal = 6.dp, vertical = 2.dp),
            ) {
                Text(
                    text     = school.replaceFirstChar { it.uppercase() }.take(4),
                    color    = BrandColors.SpellBlue,
                    fontSize = 10.sp,
                    fontWeight = FontWeight.SemiBold,
                )
            }
        }
    }
}

// ── Tab 4: Inventory ───────────────────────────────────────────────────────────

@Composable
private fun InventoryTab(
    character: Character,
    onAddItem: (InventoryItem) -> Unit,
    onRemoveItem: (String) -> Unit,
) {
    var showAddDialog by remember { mutableStateOf(false) }

    Box(Modifier.fillMaxSize()) {
        if (character.inventory.isEmpty()) {
            Column(
                Modifier.align(Alignment.Center),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Text("🎒", fontSize = 40.sp)
                Spacer(Modifier.height(8.dp))
                Text("No items yet", color = BrandColors.TextSecondary)
            }
        } else {
            LazyColumn(
                modifier       = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(horizontal = 16.dp, vertical = 16.dp),
                verticalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                items(character.inventory, key = { it.id }) { item ->
                    val dismissState = rememberSwipeToDismissBoxState(
                        confirmValueChange = {
                            if (it == SwipeToDismissBoxValue.EndToStart) {
                                onRemoveItem(item.id); true
                            } else false
                        },
                    )
                    SwipeToDismissBox(
                        state            = dismissState,
                        backgroundContent = {
                            Box(
                                Modifier
                                    .fillMaxSize()
                                    .clip(RoundedCornerShape(8.dp))
                                    .background(BrandColors.Ember)
                                    .padding(end = 16.dp),
                                contentAlignment = Alignment.CenterEnd,
                            ) {
                                Icon(Icons.Filled.Delete, "Delete", tint = BrandColors.TextPrimary)
                            }
                        },
                        modifier         = Modifier.clip(RoundedCornerShape(8.dp)),
                        enableDismissFromStartToEnd = false,
                    ) {
                        InventoryItemRow(item)
                    }
                }
                item { Spacer(Modifier.height(72.dp)) }
            }
        }

        FloatingActionButton(
            onClick        = { showAddDialog = true },
            modifier       = Modifier.align(Alignment.BottomEnd).padding(16.dp),
            containerColor = BrandColors.Ember,
            contentColor   = BrandColors.TextPrimary,
        ) {
            Icon(Icons.Filled.Add, "Add item")
        }
    }

    if (showAddDialog) {
        AddItemDialog(
            onConfirm = { onAddItem(it); showAddDialog = false },
            onDismiss = { showAddDialog = false },
        )
    }
}

@Composable
private fun InventoryItemRow(item: InventoryItem) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .background(BrandColors.Surface)
            .padding(horizontal = 16.dp, vertical = 10.dp),
        verticalAlignment     = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        if (item.equipped) {
            Box(
                Modifier
                    .clip(RoundedCornerShape(4.dp))
                    .background(BrandColors.Parchment)
                    .padding(horizontal = 4.dp, vertical = 2.dp),
            ) {
                Text("E", color = BrandColors.Background, fontSize = 10.sp, fontWeight = FontWeight.Bold)
            }
        }
        Column(Modifier.weight(1f)) {
            Text(item.name, color = BrandColors.TextPrimary, fontWeight = FontWeight.SemiBold)
            if (item.description.isNotBlank()) Text(item.description, color = BrandColors.TextSecondary, fontSize = 12.sp, maxLines = 1)
        }
        Column(horizontalAlignment = Alignment.End) {
            Text("×${item.quantity}", color = BrandColors.TextSecondary, fontSize = 12.sp)
            if (item.weight > 0.0) Text("${item.weight} lb", color = BrandColors.TextSecondary, fontSize = 11.sp)
        }
    }
}

@Composable
private fun AddItemDialog(onConfirm: (InventoryItem) -> Unit, onDismiss: () -> Unit) {
    var name     by remember { mutableStateOf("") }
    var quantity by remember { mutableStateOf("1") }
    var weight   by remember { mutableStateOf("0") }
    var desc     by remember { mutableStateOf("") }
    var equipped by remember { mutableStateOf(false) }

    AlertDialog(
        onDismissRequest = onDismiss,
        containerColor   = BrandColors.Surface,
        title  = { Text("Add Item", color = BrandColors.TextPrimary) },
        text   = {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedTextField(
                    value = name, onValueChange = { name = it },
                    label = { Text("Name *", color = BrandColors.TextSecondary) }, singleLine = true,
                    colors = itemFieldColors(),
                )
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = quantity, onValueChange = { quantity = it.filter(Char::isDigit) },
                        label = { Text("Qty", color = BrandColors.TextSecondary) }, singleLine = true,
                        modifier = Modifier.weight(1f), colors = itemFieldColors(),
                    )
                    OutlinedTextField(
                        value = weight, onValueChange = { weight = it.filter { c -> c.isDigit() || c == '.' } },
                        label = { Text("Weight (lb)", color = BrandColors.TextSecondary) }, singleLine = true,
                        modifier = Modifier.weight(1f), colors = itemFieldColors(),
                    )
                }
                OutlinedTextField(
                    value = desc, onValueChange = { desc = it },
                    label = { Text("Description", color = BrandColors.TextSecondary) }, maxLines = 3,
                    colors = itemFieldColors(),
                )
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(
                        checked = equipped, onCheckedChange = { equipped = it },
                        colors  = CheckboxDefaults.colors(checkedColor = BrandColors.Parchment),
                    )
                    Text("Equipped", color = BrandColors.TextSecondary, fontSize = 13.sp)
                }
            }
        },
        confirmButton = {
            TextButton(
                onClick  = {
                    if (name.isNotBlank()) {
                        onConfirm(InventoryItem(
                            id          = "${name.hashCode()}_${currentTimeMillis()}",
                            name        = name,
                            quantity    = quantity.toIntOrNull() ?: 1,
                            weight      = weight.toDoubleOrNull() ?: 0.0,
                            description = desc,
                            equipped    = equipped,
                        ))
                    }
                },
                enabled = name.isNotBlank(),
            ) { Text("Add", color = BrandColors.Parchment) }
        },
        dismissButton = { TextButton(onClick = onDismiss) { Text("Cancel", color = BrandColors.TextSecondary) } },
    )
}

@Composable
private fun itemFieldColors() = OutlinedTextFieldDefaults.colors(
    focusedBorderColor   = BrandColors.Parchment,
    unfocusedBorderColor = BrandColors.SurfaceLight,
    focusedTextColor     = BrandColors.TextPrimary,
    unfocusedTextColor   = BrandColors.TextPrimary,
    cursorColor          = BrandColors.Parchment,
)

// ── Tab 5: Notes ───────────────────────────────────────────────────────────────

@Composable
private fun NotesTab(character: Character, onSave: (String) -> Unit) {
    var notes by remember(character.id) { mutableStateOf(character.notes) }
    val sf = silkFont()

    LaunchedEffect(notes) {
        if (notes != character.notes) {
            delay(500)
            onSave(notes)
        }
    }

    Column(
        Modifier
            .fillMaxSize()
            .verticalScroll(rememberScrollState())
            .padding(horizontal = 16.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        // Backstory read-only card
        if (character.backstory.isNotBlank()) {
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                Text("BACKSTORY", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(10.dp))
                        .background(BrandColors.Surface)
                        .padding(12.dp),
                ) {
                    Text(character.backstory, color = BrandColors.TextPrimary, fontSize = 14.sp, lineHeight = 21.sp)
                }
            }
        }

        // Session notes — editable
        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Text("SESSION NOTES", color = BrandColors.IvoryDim, fontFamily = sf, fontSize = 9.sp)
            OutlinedTextField(
                value         = notes,
                onValueChange = { notes = it },
                placeholder   = { Text("Free-form notes for this session…", color = BrandColors.IvoryDim) },
                modifier      = Modifier
                    .fillMaxWidth()
                    .heightIn(min = 160.dp),
                minLines = 6,
                colors   = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor   = BrandColors.Parchment,
                    unfocusedBorderColor = BrandColors.SurfaceLight,
                    focusedTextColor     = BrandColors.TextPrimary,
                    unfocusedTextColor   = BrandColors.TextPrimary,
                    cursorColor          = BrandColors.Parchment,
                ),
            )
        }
    }
}
